/**
 * Centralized query key constants for React Query.
 * Use these keys to ensure consistency across queries and mutations.
 *
 * Usage:
 * ```ts
 * useQuery({ queryKey: queryKeys.planDetail.byId(planId, page, pageSize) })
 * queryClient.invalidateQueries({ queryKey: queryKeys.planDetail.all })
 * ```
 */

import type { FilterGroup } from '@/types'

export const queryKeys = {
  // Assortment Plans list
  assortmentPlans: {
    all: ['assortment-plans'] as const,
    list: (filters: {
      search?: string
      status?: string
      owner_id?: string
      hierarchy_id?: string
      season_id?: string
      page?: number
      pageSize?: number
    }) => ['assortment-plans', filters] as const,
  },

  // Plan Detail (individual plan with line items)
  planDetail: {
    all: ['plan-detail'] as const,
    byId: (planId: string) => ['plan-detail', planId] as const,
    withPagination: (
      planId: string,
      versionId: string,
      page: number,
      pageSize: number
    ) => ['plan-detail', planId, versionId, page, pageSize] as const,
    metadata: (planId: string) => ['plan-detail', 'metadata', planId] as const,
    calendar: (planId: string, isSeasonal: boolean = false) =>
      ['plan-detail', 'calendar', planId, isSeasonal] as const,
  },

  // Plan Active Version ID (cached for mutations)
  planActiveVersion: {
    byId: (planId: string) => ['plan-active-version', planId] as const,
  },

  // Product Hierarchy (departments, classes, subclasses)
  hierarchy: {
    all: ['hierarchy'] as const,
    tree: () => ['hierarchy', 'tree'] as const,
    departments: () => ['hierarchy', 'DEPARTMENT'] as const,
    classes: (departmentId: string) =>
      ['hierarchy', 'CLASS', departmentId] as const,
    subclasses: (classId: string) =>
      ['hierarchy', 'SUBCLASS', classId] as const,
    byLevel: (level: string) => ['hierarchy', 'level', level] as const,
    byLevelFiltered: (level: string, parentId: string | null) =>
      ['hierarchy', 'level', level, parentId] as const,
  },

  // SKU Search
  skuSearch: {
    all: ['skuSearch'] as const,
    byQuery: (query: string) => ['skuSearch', query] as const,
  },

  // Plan Search (for copy workflow)
  planSearch: {
    all: ['planSearch'] as const,
    byQuery: (query: string, seasonId?: string) =>
      ['planSearch', query, seasonId] as const,
  },

  // Seasons
  seasons: {
    all: ['seasons'] as const,
  },

  // Product Lists
  productLists: {
    all: ['product-lists'] as const,
  },

  // Plan Name Validation
  planNameValidation: {
    byName: (name: string) => ['plan-name-validation', name] as const,
  },

  // Plan Shares
  shares: {
    byPlanId: (planId: string) => ['shares', planId] as const,
  },

  // User Search (for sharing)
  userSearch: {
    byQuery: (query: string) => ['userSearch', query] as const,
  },

  // Audit Trail
  auditTrail: {
    byVersion: (
      planId: string,
      versionId: string,
      page: number,
      pageSize: number
    ) => ['audit-trail', planId, versionId, page, pageSize] as const,
  },

  // Plan Submissions
  submissions: {
    byPlanId: (planId: string) => ['submissions', planId] as const,
  },

  // Plan Versions
  versions: {
    byPlanId: (planId: string) => ['versions', planId] as const,
  },

  // Store Groups (top-level)
  storeGroups: {
    all: ['store-groups'] as const,
    list: (filters: Record<string, unknown>) =>
      ['store-groups', 'list', filters] as const,
    detail: (groupId: string) => ['store-groups', 'detail', groupId] as const,
    stores: (groupId: string, filters: Record<string, unknown>) =>
      ['store-groups', 'stores', groupId, filters] as const,
  },

  // Stores
  stores: {
    all: ['stores'] as const,
    list: (filters: Record<string, unknown>) =>
      ['stores', 'list', filters] as const,
    attributes: () => ['stores', 'attributes'] as const,
    attributeValues: (fieldName: string) =>
      ['stores', 'attributes', fieldName] as const,
    pogGroups: () => ['stores', 'pog-groups'] as const,
  },

  // DAG Table
  dagTable: {
    metadata: (planId: string, versionId: string) =>
      ['dag-table', 'metadata', planId, versionId] as const,
    rows: (
      planId: string,
      versionId: string,
      offset: number,
      limit: number,
      q?: string,
      filter?: FilterGroup | null
    ) =>
      [
        'dag-table',
        'rows',
        planId,
        versionId,
        offset,
        limit,
        q ?? '',
        filter ? JSON.stringify(filter) : '',
      ] as const,
    rowsPrefix: (planId: string, versionId: string) =>
      ['dag-table', 'rows', planId, versionId] as const,
  },

  // Dropdown Lookups
  dropdowns: {
    all: ['dropdowns'] as const,
  },

  // Admin Dropdown Management
  adminDropdowns: {
    all: ['admin-dropdowns'] as const,
    categories: () => ['admin-dropdowns', 'categories'] as const,
    byCategory: (category: string) => ['admin-dropdowns', category] as const,
    usage: (category: string, value: string) =>
      ['admin-dropdowns', 'usage', category, value] as const,
  },

  // Admin Warehouse Lookups
  adminWarehouseLookups: {
    all: ['admin-warehouse-lookups'] as const,
    tables: () => ['admin-warehouse-lookups', 'tables'] as const,
    entriesPrefix: (tableName: string) =>
      ['admin-warehouse-lookups', 'entries', tableName] as const,
    entries: (tableName: string, params: Record<string, unknown>) =>
      ['admin-warehouse-lookups', 'entries', tableName, params] as const,
    usage: (tableName: string, lookupKey: string) =>
      ['admin-warehouse-lookups', 'usage', tableName, lookupKey] as const,
  },

  // Admin System Settings
  adminSystemSettings: {
    all: ['admin-system-settings'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
  },

  // Comments
  comments: {
    byPlanId: (planId: string) => ['comments', planId] as const,
  },

  // Line Item Comments (keyed on stable line_ref_id)
  lineItemComments: {
    all: ['line-item-comments'] as const,
    byRefId: (refId: string) => ['line-item-comments', refId] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    list: (filters: Record<string, unknown>) =>
      ['notifications', 'list', filters] as const,
  },

  // Manager Submissions
  manager: {
    all: ['manager'] as const,
    stats: ['manager', 'stats'] as const,
    list: (filters: Record<string, unknown>) =>
      ['manager', 'list', filters] as const,
  },

  // Power BI Dashboard
  powerBi: {
    all: ['power-bi'] as const,
    dashboard: (reportType: string, reportId: string) =>
      ['power-bi', 'dashboard', reportType, reportId] as const,
  },

  // Dashboards (which reports the current user may embed)
  dashboards: {
    all: ['dashboards'] as const,
    permissionsMe: () => ['dashboards', 'permissions', 'me'] as const,
    permissionsByReport: () =>
      ['dashboards', 'permissions', 'by-report'] as const,
    permissionsByUser: () => ['dashboards', 'permissions', 'by-user'] as const,
  },

  // Commitment Letter Template
  commitmentLetterTemplate: {
    all: ['commitment-letter-template'] as const,
  },

  // Admin Users
  adminUsers: {
    all: ['admin-users'] as const,
    list: (filters: Record<string, unknown>) =>
      ['admin-users', 'list', filters] as const,
  },

  // Admin Plans (assortment access)
  adminPlans: {
    all: ['admin-plans'] as const,
    list: (filters: Record<string, unknown>) =>
      ['admin-plans', 'list', filters] as const,
    shares: (planId: string) => ['admin-plans', 'shares', planId] as const,
  },

  // Deleted Plans (undelete)
  deletedPlans: {
    all: ['deleted-plans'] as const,
    list: (filters: Record<string, unknown>) =>
      ['deleted-plans', 'list', filters] as const,
  },

  // Distribution Centers
  distributionCenters: {
    all: ['distribution-centers'] as const,
    byLine: (planId: string, versionId: string, lineId: string) =>
      ['distribution-centers', planId, versionId, lineId] as const,
  },

  // Wave Breaks
  waveBreaks: {
    byLine: (planId: string, versionId: string, lineId: string) =>
      ['wave-breaks', planId, versionId, lineId] as const,
    allForPlan: (planId: string, versionId: string) =>
      ['wave-breaks', planId, versionId] as const,
  },

  // Retail Pricing
  retailPricing: {
    byLine: (planId: string, versionId: string, lineId: string) =>
      ['retail-pricing', planId, versionId, lineId] as const,
  },

  // Store Forecast
  storeForecast: {
    byLine: (planId: string, versionId: string, lineId: string) =>
      ['store-forecast', planId, versionId, lineId] as const,
  },

  // Children (parent-child SKU relationships)
  children: {
    byParent: (planId: string, versionId: string, lineId: string) =>
      ['children', planId, versionId, lineId] as const,
  },

  // MFPs
  mfps: {
    all: ['mfps'] as const,
    list: () => ['mfps', 'list'] as const,
    byPlan: (planId: string) => ['mfps', 'plan', planId] as const,
  },

  // PIAB (POG In A Box)
  piab: {
    byParent: (planId: string, versionId: string, lineId: string) =>
      ['piab', planId, versionId, lineId] as const,
  },

  // Parent Line Search
  parentLineSearch: {
    byQuery: (planId: string, versionId: string, q: string) =>
      ['parent-line-search', planId, versionId, q] as const,
  },

  // Multiloc
  multiloc: {
    byLine: (planId: string, versionId: string, lineId: string) =>
      ['multiloc', planId, versionId, lineId] as const,
    lines: (planId: string, versionId: string, q?: string) =>
      ['multiloc', 'lines', planId, versionId, q ?? ''] as const,
    allForPlan: (planId: string, versionId: string) =>
      ['multiloc', planId, versionId] as const,
  },

  // Store Models
  storeModels: {
    all: ['store-models'] as const,
    list: (filters: Record<string, unknown>) =>
      ['store-models', 'list', filters] as const,
    search: (query: string) => ['store-models', 'search', query] as const,
    stores: (modelId: string, filters: Record<string, unknown>) =>
      ['store-models', 'stores', modelId, filters] as const,
    plans: (modelId: string, filters: Record<string, unknown>) =>
      ['store-models', 'plans', modelId, filters] as const,
    detail: (modelId: string) => ['store-models', 'detail', modelId] as const,
    groups: (modelId: string) => ['store-models', 'groups', modelId] as const,
    groupMembers: (
      modelId: string,
      groupId: string,
      filters: Record<string, unknown>
    ) => ['store-models', 'group-members', modelId, groupId, filters] as const,
  },
  // Calendars
  calendars: {
    all: ['calendars'] as const,
    list: (filters: Record<string, unknown>) =>
      ['calendars', 'list', filters] as const,
    listInfinite: (search: string) =>
      ['calendars', 'list-infinite', search] as const,
    detail: (id: string) => ['calendars', 'detail', id] as const,
    grid: (id: string) => ['calendars', 'grid', id] as const,
    events: (calendarId: string) =>
      ['calendars', 'events', calendarId] as const,
  },

  // Milestone Rules
  milestoneRules: {
    all: ['milestone-rules'] as const,
  },

  // Images (admin gallery)
  images: {
    all: ['images'] as const,
    list: (filters: Record<string, unknown>) =>
      ['images', 'list', filters] as const,
    detail: (imageId: string) => ['images', 'detail', imageId] as const,
  },

  // Hierarchy Shares
  hierarchyShares: {
    all: ['hierarchy-shares'] as const,
    byNodeId: (id: string) => ['hierarchy-shares', id] as const,
  },

  // Plan Browse
  browse: {
    all: ['browse'] as const,
    divisions: (q?: string) => ['browse', 'divisions', q] as const,
    departments: (divisionId: string, q?: string) =>
      ['browse', 'departments', divisionId, q] as const,
    calendars: (departmentId: string) =>
      ['browse', 'calendars', departmentId] as const,
    calendarEvents: (departmentId: string, calendarId: string) =>
      ['browse', 'calendar-events', departmentId, calendarId] as const,
    plans: (filters: Record<string, unknown>) =>
      ['browse', 'plans', filters] as const,
  },

  // Line Item Images
  lineItemImages: {
    byLine: (planId: string, versionId: string, lineId: string) =>
      ['line-item-images', planId, versionId, lineId] as const,
  },

  // Plan Column Order
  columnOrder: {
    byPlan: (planId: string) => ['column-order', planId] as const,
  },

  // Plan Column Widths (user + plan scoped)
  columnWidths: {
    byPlan: (planId: string) => ['column-widths', planId] as const,
  },

  // Saved Column Views (user-scoped)
  columnViews: {
    all: ['column-views'] as const,
  },

  // Saved Power BI report filters (user + plan + report type scoped)
  reportFilters: {
    list: (planId: string, reportType: string) =>
      ['report-filters', planId, reportType] as const,
  },
} as const
